import { ComponentType, ReactNode, forwardRef, useState } from "react";
import { Check, ChevronDown } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";

interface FilterPillProps {
  active: boolean;
  label?: string;
  children: ReactNode;
}

/** Gray-bordered toolbar pill, `Label: value ▾`, shared by the inbox and genie filter bars. */
export const FilterPill = forwardRef<HTMLButtonElement, FilterPillProps & React.ButtonHTMLAttributes<HTMLButtonElement>>(
  ({ active, label, children, className, ...props }, ref) => (
    <button
      ref={ref}
      type="button"
      {...props}
      className={cn(
        "max-w-[15rem] shrink-0 p-px rounded-xl bg-[#E5EAF0] hover:bg-genie-primary/40 transition-colors",
        className,
      )}
    >
      <span
        className={cn(
          "min-h-[42px] sm:min-h-0 flex items-center gap-1.5 rounded-[11px] px-3.5 py-[9px] text-sm",
          active ? "bg-gray-100 text-gray-900 font-semibold" : "bg-white text-gray-600",
        )}
      >
        {label && <span className="text-gray-400 font-normal">{label}:</span>}
        {children}
        <ChevronDown className="h-4 w-4 shrink-0 text-gray-400" />
      </span>
    </button>
  ),
);
FilterPill.displayName = "FilterPill";

export type FilterOptionIcon = ComponentType<{ className?: string }>;

export interface FilterOption<T extends string> {
  value: T | null;
  label: string;
  Icon?: FilterOptionIcon;
}

interface OptionPillProps<T extends string> {
  label?: string;
  valueLabel: string;
  ValueIcon?: FilterOptionIcon;
  active: boolean;
  options: FilterOption<T>[];
  selected: T | null;
  onSelect: (value: T | null) => void;
}

/** A FilterPill that opens a single-choice option list. */
export function OptionPill<T extends string>({ label, valueLabel, ValueIcon, active, options, selected, onSelect }: OptionPillProps<T>) {
  const [open, setOpen] = useState(false);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <FilterPill active={active} label={label}>
          {ValueIcon && <ValueIcon className="h-4 w-4 shrink-0 text-genie-primary" />}
          <span className="truncate">{valueLabel}</span>
        </FilterPill>
      </PopoverTrigger>
      <PopoverContent align="start" sideOffset={6} className="w-56 max-h-80 overflow-y-auto p-1">
        {options.map((option) => {
          const isOn = option.value === selected;
          return (
            <button
              key={option.value ?? "all"}
              type="button"
              onClick={() => {
                onSelect(option.value);
                setOpen(false);
              }}
              className="w-full min-h-[44px] sm:min-h-[36px] flex items-center gap-2 rounded-md px-3 text-left text-sm text-[#222B45] hover:bg-[#FAFCFF]"
            >
              {option.Icon && <option.Icon className="h-4 w-4 shrink-0 text-genie-primary" />}
              <span className={cn("flex-1 truncate", isOn && "font-semibold")}>{option.label}</span>
              {isOn && <Check className="h-4 w-4 shrink-0 text-genie-primary" />}
            </button>
          );
        })}
      </PopoverContent>
    </Popover>
  );
}
